/** PROBATION-REVIEW-001 P3 — HR Manager decision dialog (approve / return) with leave-impact acknowledgement. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../ui/dialog';
import { fmtDate } from './probationUi';
import { API, h, err, MIN_REASON, RATINGS } from './probationApi';

export default function DecisionDialog({ open, onOpenChange, review, employee, onDecided }) {
  const [note, setNote] = useState('');
  const [impact, setImpact] = useState(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const rec = review?.recommendation;
  const confirming = rec?.outcome === 'confirmed';

  useEffect(() => {
    if (!open || !review) return;
    setNote(''); setAck(false); setImpact(null);
    if (confirming) axios.get(`${API}/api/hr/probation/employees/${review.employee_id}/leave-impact`, { headers: h() }).then(r => setImpact(r.data)).catch(() => setImpact({ changed: false, rows: [], unavailable: true }));
  }, [open, review, confirming]);
  if (!review || !rec) return null;

  const decide = async (decision) => {
    try { setBusy(true);
      await axios.post(`${API}/api/hr/probation/reviews/${review.id}/decision`, { decision, note: note.trim(), acknowledge_leave_impact: ack }, { headers: h() });
      toast.success(decision === 'approved' ? (confirming ? 'Probation confirmed — Employment Type is now Permanent' : `Probation extended to ${fmtDate(rec.extension?.revised_end_date)}`) : 'Review returned to the Reporting Manager');
      onOpenChange(false); onDecided?.(); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const avg = (RATINGS.reduce((a, r) => a + (rec.ratings?.[r.key] || 0), 0) / RATINGS.length).toFixed(1);
  const needAck = confirming && impact?.changed && !ack;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0 overflow-hidden" data-testid="decision-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100 text-left">
          <DialogTitle className="text-[16px] font-semibold text-slate-900">HR decision · {employee?.full_name || review.employee_name}</DialogTitle>
          <DialogDescription className="text-[12.5px] text-slate-500">Cycle {review.cycle_no} · submitted by {rec.submitted_by_name} · {fmtDate(rec.submitted_at?.slice(0, 10))}{rec.version > 1 ? ` · revision ${rec.version}` : ''}</DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4 max-h-[62vh] overflow-y-auto">
          <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-3.5 py-2.5" data-testid="decision-recommendation">
            <div>
              <p className="prb-metric-label">Manager recommends</p>
              <p className={`text-[14px] font-bold ${confirming ? 'text-emerald-700' : 'text-indigo-700'}`}>{confirming ? 'Confirm probation' : `Extend to ${fmtDate(rec.extension?.revised_end_date)}`}</p>
              {!confirming && <p className="text-[12px] text-slate-600 mt-0.5">{rec.extension?.reason}</p>}
            </div>
            <div className="text-right"><p className="prb-metric-label">Avg rating</p><p className="text-[18px] font-bold text-slate-900 leading-tight">{avg}<span className="text-[11px] text-slate-400 font-medium"> / 5</span></p></div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="decision-ratings">
            {RATINGS.map(r => (
              <div key={r.key} className="prb-metric tone-violet">
                <p className="prb-metric-label">{r.label}</p>
                <div className="flex items-center gap-1 mt-1">{[1, 2, 3, 4, 5].map(n => <span key={n} className={`prb-dotbar ${n <= (rec.ratings?.[r.key] || 0) ? 'lit' : ''}`} />)}<span className="ml-1 text-[12.5px] font-bold text-slate-800">{rec.ratings?.[r.key]}</span></div>
              </div>
            ))}
          </div>

          <dl className="space-y-2 text-[13px]" data-testid="decision-feedback">
            {[['Strengths', rec.strengths], ['Areas to improve', rec.improvement_areas], ['Overall comments', rec.comments]].map(([k, v]) => (
              <div key={k}><dt className="prb-metric-label">{k}</dt><dd className="text-slate-700 leading-relaxed mt-0.5">{v}</dd></div>
            ))}
          </dl>

          {confirming && (
            <div className={`rounded-xl border px-3.5 py-2.5 ${impact?.changed ? 'border-amber-200 bg-amber-50/60' : 'border-slate-100 bg-slate-50/60'}`} data-testid="decision-leave-impact">
              <p className="text-[12.5px] font-semibold text-slate-800">Employment Type will change to <span className="text-violet-700">Permanent</span></p>
              {!impact ? <p className="text-[12px] text-slate-500 mt-0.5">Checking leave entitlement impact…</p>
                : impact.unavailable ? <p className="text-[12px] text-slate-500 mt-0.5">Leave impact preview unavailable.</p>
                : impact.changed ? (
                  <div className="mt-1.5 space-y-1.5">
                    {impact.rows.filter(r => r.changed).map(r => <p key={r.leave_type_id} className="text-[12px] text-amber-800">{r.name}: {r.before ?? '—'} → <span className="font-semibold">{r.after ?? '—'}</span> days ({impact.year})</p>)}
                    <label className="flex items-center gap-2 text-[12.5px] text-slate-700 cursor-pointer"><input type="checkbox" className="accent-violet-600" checked={ack} onChange={e => setAck(e.target.checked)} data-testid="decision-ack-leave" />I acknowledge this year's leave entitlement changes</label>
                  </div>
                ) : <p className="text-[12px] text-slate-500 mt-0.5">No change to this year's leave entitlement. Used and approved leave is never recalculated.</p>}
            </div>
          )}

          <label className="block"><span className="text-[12.5px] font-medium text-slate-600 mb-1 block">HR note <span className="text-slate-400">(required to return, min {MIN_REASON})</span></span>
            <textarea className="prb-input !h-auto min-h-[56px] py-2 resize-y" rows={2} value={note} onChange={e => setNote(e.target.value)} data-testid="decision-note" /></label>
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 bg-white">
          <button type="button" className="prb-btn-ghost" onClick={() => onOpenChange(false)} data-testid="decision-cancel-btn">Cancel</button>
          <button type="button" className="prb-btn-ghost !text-amber-700 !border-amber-200 hover:!bg-amber-50" disabled={busy || note.trim().length < MIN_REASON} onClick={() => decide('returned')} data-testid="decision-return-btn">Return for revision</button>
          <button type="button" className="prb-btn-primary" disabled={busy || needAck || (confirming && !impact)} onClick={() => decide('approved')} data-testid="decision-approve-btn">{confirming ? 'Approve & confirm' : 'Approve extension'}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
